import { useCallback, useRef, useState } from "react";

import toast from "components/ui/toast/Toast";
import { useUpdateLeads } from "lib/hooks/api/leads/useUpdateLeads";

// Local leadId -> stageId override so a dragged card renders in its new column
// immediately, ahead of useUpdateLeads's own (onSuccess-only) cache patch.
export function useLeadStageMove() {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // A card can be dragged again while its previous move is still in flight. Letting both
  // requests run means the server keeps whichever one arrives last, which need not be the
  // stage the user picked last — so only one request per lead is ever open, and a newer
  // target waits for it. Refs, not state: this must not trigger a re-render.
  const inFlight = useRef(new Set<string>());
  const queued = useRef(new Map<string, string>());

  const { mutate } = useUpdateLeads({
    onSuccess: () => toast.success("Stage updated successfully"),
    onError: () => toast.error("Failed to update stage"),
  });

  const sendRef = useRef<(leadId: string, stageId: string) => void>(() => undefined);

  const send = useCallback(
    (leadId: string, stageId: string) => {
      inFlight.current.add(leadId);

      mutate(
        { updateLeadsRequest: { ids: [leadId], updateData: { stageId } } },
        {
          onError: () =>
            setOverrides((prev) => {
              // Only clear the override if it still belongs to this call — a newer move of
              // the same lead may have already overwritten it while this request was in flight.
              if (prev[leadId] !== stageId) return prev;
              const next = { ...prev };
              delete next[leadId];
              return next;
            }),
          onSettled: () => {
            inFlight.current.delete(leadId);
            const pending = queued.current.get(leadId);
            if (pending === undefined) return;
            queued.current.delete(leadId);
            // Nothing to do if the queued target is what we just wrote.
            if (pending !== stageId) sendRef.current(leadId, pending);
          },
        },
      );
    },
    [mutate],
  );

  sendRef.current = send;

  const moveLead = useCallback(
    (leadId: string, stageId: string) => {
      setOverrides((prev) => ({ ...prev, [leadId]: stageId }));

      if (inFlight.current.has(leadId)) {
        // Overwrites any earlier queued target: only the newest intent matters.
        queued.current.set(leadId, stageId);
        return;
      }

      send(leadId, stageId);
    },
    [send],
  );

  return { overrides, moveLead };
}
